import type { IntervalConfig, ReportMetadata, ServiceAssignment, ServiceItem } from '../../types';
import {
    type BerlinEventInput,
    alignSeriesStart,
    begehungTitel,
    buildDescriptionLines,
    generateBerlinIcs,
    inspectionSeriesAnchor,
    intervalToRrule,
    normalizeTime,
    projectStartDate,
    seriesStartFromAnchor,
    sortedPreferredWeekdays,
    wochenMerker,
} from '../calendarService';
import { formatDateLocal, heuteBerlin, parseDateLocal, resolveIntervalConfig } from '../dateService';
import {
    BEREICH_ZUORDNUNGEN,
    KICKOFF_SA_DAUER_MIN,
    KICKOFF_SA_MAX_PLATZ,
    KICKOFF_SA_START,
    type KickoffBereich,
    type KickoffHinweis,
    type KickoffPlan,
    type KickoffTermin,
    type KickoffTerminArt,
} from './kickoffTypes';

/**
 * Projekt-Kickoff, Termin-Logik: Welche Kalendereinträge bekommt EIN
 * Empfänger für EIN Projekt in seinem Bereich? Rein, ohne Datenbank und
 * ohne Oberfläche — Mail, Popup und Kalenderdatei lesen denselben Plan,
 * damit sie sich nicht widersprechen. Bauplan: docs/projekt-kickoff-plan.md
 * (§ 4.1, § 4.2a).
 *
 * Turnus, Wochentage und Datumsrechnung kommen aus `dateService` und
 * `calendarService` wie bei Urlaubsplaner und Hub-Download. Die App bleibt
 * für die Fälligkeit führend; die Kalenderserie ist die Erinnerung.
 */

const UID_DOMAIN = 'sigeko-app';

/** Fester Bezeichner je Terminart — Betreff im Kalender und Mailtext. */
const TERMIN_BEZEICHNUNG: Record<KickoffTerminArt, string> = {
    vorank: 'Vorankündigung',
    sigeplan: 'SiGe-Plan',
    begehung: 'Begehung',
    einweisung: 'Einweisung SiGe-Plan',
};

const UID_SUFFIX: Record<KickoffTerminArt, string> = {
    vorank: '-vorank',
    sigeplan: '-sigeplan',
    // Begehung ohne Suffix: dieselbe UID wie der Kalender-Download im Hub,
    // sonst stünde die Serie nach beiden Wegen doppelt im Kalender.
    begehung: '',
    einweisung: '-einweisung',
};

const WOCHENTAG_KURZ = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const ALLE_WOCHENTAGE = [1, 2, 3, 4, 5, 6, 0];

/** Vorankündigung spätestens zwei Wochen vor Baustart (§ 2 Abs. 2 BaustellV). */
const VORANK_TAGE_VOR_BAUSTART = 14;
const VORANK_DAUER_MIN = 30;
const BEGEHUNG_DAUER_MIN = 60;
const EINWEISUNG_DAUER_MIN = 60;

// ── LV und Bereich ──────────────────────────────────────────────────────────

/** LV-Zuordnung → Bereich; Zuordnungen ohne Kalendertermin liefern `null`. */
export const bereichFuerAssignment = (a: ServiceAssignment | undefined): 'planung' | 'ausfuehrung' | null => {
    if (!a) return null;
    if (BEREICH_ZUORDNUNGEN.planung.includes(a)) return 'planung';
    if (BEREICH_ZUORDNUNGEN.ausfuehrung.includes(a)) return 'ausfuehrung';
    return null;
};

const umfasst = (bereich: KickoffBereich, teil: 'planung' | 'ausfuehrung'): boolean =>
    bereich === 'alle' || bereich === teil;

/**
 * Felder einer `projects`-Zeile, die der Kickoff liest. Bewusst locker
 * typisiert: Dienst und Hub reichen Teilabfragen und Altbestand durch.
 */
interface KickoffProjektZeile {
    id?: unknown;
    project_number?: unknown;
    name?: unknown;
    start_date?: unknown;
    /** Geplanter nächster Termin — erster Rang des Begehungsankers. */
    next_scheduled_date?: unknown;
    data?: { metadata?: unknown } | null;
    /**
     * LV aus der Tabelle, Join `project_services(assignment,is_optional)`.
     * Der Blob trägt kein LV: saveProjectToCloud leert `globalData.services`
     * vor dem Schreiben.
     */
    project_services?: unknown;
}

interface KickoffLvZeile {
    assignment?: unknown;
    is_optional?: unknown;
}

const alsZeile = (project: unknown): KickoffProjektZeile =>
    project && typeof project === 'object' ? (project as KickoffProjektZeile) : {};

const leseMeta = (project: unknown): Partial<ReportMetadata> =>
    (alsZeile(project).data?.metadata as Partial<ReportMetadata> | undefined) || {};

/**
 * Zuordnungen der beauftragten LV-Positionen. NEP-Positionen (`is_optional`)
 * stehen nur mit Preis im Angebot und sind nicht beauftragt — wie Aufgaben
 * und Dashboard fordern wir für sie keinen Termin ein.
 *
 * Quelle ist `project_services`, sobald die Zeile Positionen mitbringt: im
 * Blob steht nach jedem Speichern eine leere Liste (Vorfall 14.10.2026:
 * jede Kickoff-Mail meldete „keine Kalendertermine"). Der Blob bleibt
 * Rückfall für Altbestand, der nie über saveProjectToCloud lief.
 */
const lvZuordnungen = (project: unknown): Set<ServiceAssignment> => {
    const out = new Set<ServiceAssignment>();
    const tabelle = alsZeile(project).project_services;
    if (Array.isArray(tabelle) && tabelle.length > 0) {
        tabelle.forEach((z: KickoffLvZeile | null | undefined) => {
            if (!z || z.is_optional === true || typeof z.assignment !== 'string' || !z.assignment) return;
            out.add(z.assignment as ServiceAssignment);
        });
        return out;
    }
    const services = leseMeta(project).globalData?.services;
    if (!Array.isArray(services)) return out;
    services.forEach((s: ServiceItem | null | undefined) => {
        if (!s || s.is_optional === true || !s.assignment) return;
        out.add(s.assignment);
    });
    return out;
};

/**
 * Braucht der Empfänger einen Samstagsplatz? Nur mit SiGe-Plan im LV und
 * nur, wenn er die Planung (mit) übernimmt.
 */
export const braucheSaPlatz = (project: unknown, bereich: KickoffBereich): boolean =>
    umfasst(bereich, 'planung') && lvZuordnungen(project).has('sigeplan');

// ── Datum und Uhrzeit ───────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, '0');

const normDatum = (value: unknown): string | null => {
    const d = parseDateLocal(typeof value === 'string' ? value : null);
    return d ? formatDateLocal(d) : null;
};

const addTage = (datum: string, tage: number): string => {
    const d = parseDateLocal(datum)!;
    d.setDate(d.getDate() + tage);
    return formatDateLocal(d);
};

const wochentag = (datum: string): number => parseDateLocal(datum)!.getDay();

/** „Do. 05.11.2026" — Kurzform für Mail, Popup und Hinweise. */
const datumAnzeige = (datum: string): string => {
    const d = parseDateLocal(datum);
    if (!d) return datum;
    return `${WOCHENTAG_KURZ[d.getDay()]}. ${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const tageAnzeige = (tage: number[]): string => tage.map(d => WOCHENTAG_KURZ[d]).join(' + ');

/** „A", „A und B", „A, B und C". */
const aufzaehlung = (teile: string[]): string =>
    teile.length <= 1 ? (teile[0] || '') : `${teile.slice(0, -1).join(', ')} und ${teile[teile.length - 1]}`;

const SA_START_MIN = (() => {
    const [h, m] = KICKOFF_SA_START.split(':').map(Number);
    return h * 60 + m;
})();

/** Gültiger Samstagsplatz: ganze Zahl 0 … KICKOFF_SA_MAX_PLATZ (23:30), wie die SQL-Grenze. */
const istSaPlatz = (platz: number | null | undefined): platz is number =>
    typeof platz === 'number' && Number.isInteger(platz) && platz >= 0 && platz <= KICKOFF_SA_MAX_PLATZ;

/**
 * Uhrzeit eines Samstagsplatzes: 0 → 08:00, 1 → 08:30, 8 → 12:00 … 31 →
 * 23:30. Jede SiGe-Plan-Serie eines Koordinators bekommt ihren eigenen Platz,
 * sonst lägen alle Serien samstags um 08:00 übereinander (§ 4.2a).
 *
 * Ungültige Plätze liefern nur der Vollständigkeit halber 08:00 — eine Serie
 * entsteht daraus nicht: `buildKickoffPlan` prüft den Platz vorher und
 * meldet `sigeplan_ohne_platz`.
 */
export const saPlatzUhrzeit = (platz: number): string => {
    const p = istSaPlatz(platz) ? platz : 0;
    const minuten = SA_START_MIN + p * KICKOFF_SA_DAUER_MIN;
    return `${pad2(Math.floor(minuten / 60))}:${pad2(minuten % 60)}`;
};

// ── Begehungsserie ──────────────────────────────────────────────────────────

/** Kurztext der Serie: „jeden Di + Do 08:00", „alle 2 Wochen Mo 08:00" … */
const turnusAnzeige = (config: IntervalConfig, datum: string, uhrzeit: string): string => {
    const tage = sortedPreferredWeekdays(config);
    const serienTage = tageAnzeige(tage.length ? tage : [wochentag(datum)]);
    switch (config.type) {
        case 'daily': return `täglich ${uhrzeit}`;
        case 'weekly': return `jeden ${serienTage} ${uhrzeit}`;
        case 'biweekly': return `alle 2 Wochen ${serienTage} ${uhrzeit}`;
        case 'monthly': return `monatlich am ${parseDateLocal(datum)!.getDate()}. ${uhrzeit}`;
        case 'twice_monthly': return `am 1. und 15. des Monats ${uhrzeit}`;
        case 'custom': {
            const n = config.customDays;
            if (typeof n === 'number' && Number.isInteger(n) && n >= 1) {
                return n === 1 ? `täglich ${uhrzeit}` : `alle ${n} Tage ${uhrzeit}`;
            }
            // Ohne Tageszahl schreibt intervalToRrule eine einfache Wochenserie.
            return `jeden ${WOCHENTAG_KURZ[wochentag(datum)]} ${uhrzeit}`;
        }
        default: return uhrzeit;
    }
};

/**
 * Wochentage, an denen die Serie im Kalender JEDE Woche liegt — Grundlage
 * des Überschneidungshinweises. Monatliche Serien und „alle n Tage" mit n
 * ungleich Vielfachem von 7 wandern über die Woche und bekommen keinen.
 */
const serienWochentage = (config: IntervalConfig, datum: string): number[] => {
    const tage = sortedPreferredWeekdays(config);
    switch (config.type) {
        case 'daily': return ALLE_WOCHENTAGE;
        case 'weekly':
        case 'biweekly': return tage.length ? tage : [wochentag(datum)];
        case 'custom': {
            const n = config.customDays;
            if (typeof n !== 'number' || !Number.isInteger(n) || n < 1) return [wochentag(datum)];
            if (n === 1) return ALLE_WOCHENTAGE;
            return n % 7 === 0 ? [wochentag(datum)] : [];
        }
        default: return [];
    }
};

/** Ein anderes Projekt des Empfängers, gegen das die neue Begehungsserie geprüft wird. */
export interface KickoffAnderesProjekt {
    id: string;
    projectNumber: string;
    projectName: string;
    /** `IntervalType`; `manual` hat keine Serie und überschneidet nichts. */
    intervalType?: string;
    /** 0 = So … 6 = Sa. Ohne Wochentage (und nicht täglich) ist kein Vergleich möglich. */
    weekdays?: number[];
    /** `HH:MM`; fehlt = 08:00, der Standard der App. */
    uhrzeit?: string;
}

const andereWochentage = (p: KickoffAnderesProjekt): number[] => {
    if (p.intervalType === 'manual') return [];
    if (p.intervalType === 'daily') return ALLE_WOCHENTAGE;
    return sortedPreferredWeekdays({ type: 'weekly', preferredWeekdays: p.weekdays });
};

/**
 * Hinweise „Überschneidung mit Projekt …": gleiche Uhrzeit und mindestens
 * ein gemeinsamer Wochentag. Verschoben wird nicht — die echte Reihenfolge
 * entscheidet die Tourenplanung (§ 4.2a).
 */
const ueberschneidungen = (
    projectId: string,
    eigeneTage: number[],
    uhrzeit: string,
    andere: KickoffAnderesProjekt[] | undefined,
): KickoffHinweis[] => {
    if (eigeneTage.length === 0 || !Array.isArray(andere)) return [];
    const out: KickoffHinweis[] = [];
    const gemeldet = new Set<string>();
    andere.forEach(p => {
        if (!p || p.id === projectId || gemeldet.has(p.id)) return;
        if (normalizeTime(p.uhrzeit) !== uhrzeit) return;
        const fremd = andereWochentage(p);
        const gemeinsam = eigeneTage.filter(d => fremd.includes(d));
        if (gemeinsam.length === 0) return;
        gemeldet.add(p.id);
        const bezeichnung = [p.projectNumber, p.projectName].map(s => String(s ?? '').trim()).filter(Boolean).join(' ');
        out.push({
            art: 'ueberschneidung',
            text: `Überschneidung mit Projekt ${bezeichnung || p.id} (${tageAnzeige(gemeinsam)} ${uhrzeit})`,
        });
    });
    return out;
};

// ── Plan ────────────────────────────────────────────────────────────────────

export interface KickoffPlanOptionen {
    bereich: KickoffBereich;
    /**
     * Halbstunden-Platz der Samstagsserie aus der Quittungszeile (0 … 31).
     * `null` oder ungültig: KEINE SiGe-Plan-Serie, Hinweis
     * `sigeplan_ohne_platz` — eine geratene Uhrzeit kollidierte im Kalender
     * mit einer anderen Serie, und ein späterer Abruf mit vergebenem Platz
     * lieferte eine andere Uhrzeit für dieselbe UID.
     */
    saPlatz: number | null;
    /** `YYYY-MM-DD`; Standard ist der heutige Tag in Berlin. */
    heute?: string;
    /** Weitere Projekte des Empfängers für den Hinweis „Überschneidung". */
    andereProjekte?: KickoffAnderesProjekt[];
}

/**
 * Alle Kalendereinträge EINES Projekts für EINEN Empfänger und Bereich.
 *
 * `project` ist eine Zeile aus `projects` (`id`, `project_number`,
 * `start_date`, `next_scheduled_date`, `data.metadata`, Join
 * `project_services`). Hinweise zu einem
 * vorhandenen Termin (Frist überschritten, Überschneidung) stehen am Termin;
 * Hinweise ohne Termin (Baustart fehlt, Turnus manuell, SiGe-Plan ohne
 * Samstagsplatz) stehen am Plan.
 */
export const buildKickoffPlan = (project: unknown, opt: KickoffPlanOptionen): KickoffPlan => {
    const meta = leseMeta(project);
    const gd = meta.globalData;
    const heute = normDatum(opt.heute) || heuteBerlin();
    const zeile = alsZeile(project);
    const projectId = String(zeile.id ?? '');
    const nummer = String(zeile.project_number || meta.projectNumber || '').trim();
    const name = String(meta.projectName || zeile.name || '').trim();
    const adresse = meta.projectAddress || '';
    const baustart = projectStartDate(project);
    const bauende = normDatum(gd?.endDate);

    const zuordnungen = lvZuordnungen(project);
    const planung = umfasst(opt.bereich, 'planung');
    const ausfuehrung = umfasst(opt.bereich, 'ausfuehrung');

    const beschreibungBasis = buildDescriptionLines({
        ...meta,
        projectName: name,
        projectNumber: nummer,
    } as ReportMetadata);

    const termin = (
        art: KickoffTerminArt,
        felder: Pick<KickoffTermin, 'datum' | 'uhrzeit' | 'dauerMin' | 'anzeige'> & { rrule?: string; ganztags?: boolean },
        vorspann: string[],
        hinweise: KickoffHinweis[] = [],
    ): KickoffTermin => {
        const kopf = [nummer, TERMIN_BEZEICHNUNG[art]].filter(Boolean).join(' ');
        return {
            art,
            uid: `${projectId}${UID_SUFFIX[art]}@${UID_DOMAIN}`,
            // Begehung: derselbe Betreff wie der Hub-Download (gleiche UID).
            titel: art === 'begehung'
                ? begehungTitel(nummer, name, !!felder.ganztags)
                : (name ? `${kopf} – ${name}` : kopf),
            datum: felder.datum,
            uhrzeit: felder.uhrzeit,
            dauerMin: felder.dauerMin,
            ...(felder.ganztags ? { ganztags: true } : {}),
            ...(felder.rrule ? { rrule: felder.rrule } : {}),
            beschreibung: [...vorspann, ...hinweise.map(h => h.text), '', ...beschreibungBasis],
            ...(adresse ? { ort: adresse } : {}),
            anzeige: felder.anzeige,
            hinweise,
        };
    };

    const termine: KickoffTermin[] = [];
    const planHinweise: KickoffHinweis[] = [];
    const ohneBaustart: string[] = [];
    const uhrzeitProjekt = normalizeTime(meta.time);

    // Vorankündigung: Baustart − 14 Tage, am Wochenende der Freitag davor.
    if (planung && zuordnungen.has('vorank')) {
        if (!baustart) {
            ohneBaustart.push('Vorankündigung');
        } else {
            let datum = addTage(baustart, -VORANK_TAGE_VOR_BAUSTART);
            const wd = wochentag(datum);
            if (wd === 6) datum = addTage(datum, -1);
            else if (wd === 0) datum = addTage(datum, -2);
            const hinweise: KickoffHinweis[] = [];
            if (datum < heute) {
                hinweise.push({
                    art: 'frist_ueberschritten',
                    text: `Frist Vorankündigung überschritten (fällig ${datumAnzeige(datum)}) – bitte sofort erledigen.`,
                });
                datum = heute;
            }
            termine.push(termin('vorank', {
                datum,
                uhrzeit: '08:00',
                dauerMin: VORANK_DAUER_MIN,
                anzeige: datumAnzeige(datum),
            }, [
                `Vorankündigung nach § 2 Abs. 2 BaustellV spätestens zwei Wochen vor Baustart (${datumAnzeige(baustart)}) übermitteln.`,
            ], hinweise));
        }
    }

    // SiGe-Plan: Samstagsserie ohne Ende, eigener Halbstunden-Platz. Braucht
    // keinen Baustart — fortgeschrieben wird ab sofort.
    if (planung && zuordnungen.has('sigeplan') && !istSaPlatz(opt.saPlatz)) {
        planHinweise.push({
            art: 'sigeplan_ohne_platz',
            text: 'SiGe-Plan-Serie noch nicht eingeplant – bitte „Mail erneut senden“ oder das Popup später erneut öffnen.',
        });
    } else if (planung && zuordnungen.has('sigeplan')) {
        const uhrzeit = saPlatzUhrzeit(opt.saPlatz);
        const datum = alignSeriesStart(heute, { type: 'weekly', preferredWeekdays: [6] });
        termine.push(termin('sigeplan', {
            datum,
            uhrzeit,
            dauerMin: KICKOFF_SA_DAUER_MIN,
            rrule: 'FREQ=WEEKLY;BYDAY=SA',
            anzeige: `jeden Sa ${uhrzeit}`,
        }, ['SiGe-Plan fortschreiben – fester Samstagstermin, Serie ohne Ende.']));
    }

    // Begehungen: Serie nach Turnus ohne Ende (Entscheid 11.10.). Wochentag
    // und Phase hängen am festen Anker (geplanter nächster Termin, sonst
    // Baustart), nie am Abrufdatum — Mail, Popup und Hub-Download liefern
    // so dieselbe Serie.
    if (ausfuehrung && zuordnungen.has('inspection')) {
        const config = resolveIntervalConfig(meta as ReportMetadata);
        const rrule = intervalToRrule(config);
        const anker = inspectionSeriesAnchor(project);
        if (!rrule) {
            planHinweise.push({
                art: 'turnus_manuell',
                text: 'Turnus manuell – Begehungen nach Absprache, keine Kalenderserie.',
            });
        } else if (!anker) {
            ohneBaustart.push('Begehungen');
        } else {
            const start = seriesStartFromAnchor(anker, heute, config);
            // Ohne feste Wochentage: ganztägiger Montags-Merker, frei —
            // kann sich mit nichts überschneiden.
            const merker = wochenMerker(config, start);
            if (merker) {
                const anzeige = `${config.type === 'biweekly' ? 'alle 2 Wochen' : 'jede Woche'}, Merker montags (ganztägig)`;
                termine.push(termin('begehung', {
                    datum: merker.datum,
                    uhrzeit: uhrzeitProjekt,
                    dauerMin: BEGEHUNG_DAUER_MIN,
                    rrule: merker.rrule,
                    ganztags: true,
                    anzeige,
                }, [`Begehung ${anzeige}. Den Tag legt die Tourenplanung fest; fällig ist, was die App anzeigt.`]));
            } else {
                const anzeige = turnusAnzeige(config, start, uhrzeitProjekt);
                const hinweise = ueberschneidungen(
                    projectId,
                    serienWochentage(config, start),
                    uhrzeitProjekt,
                    opt.andereProjekte,
                );
                termine.push(termin('begehung', {
                    datum: start,
                    uhrzeit: uhrzeitProjekt,
                    dauerMin: BEGEHUNG_DAUER_MIN,
                    rrule,
                    anzeige,
                }, [`Begehung ${anzeige}. Fällig ist, was die App anzeigt – die Serie ist die Erinnerung.`], hinweise));
            }
        }
    }

    // Einweisung in den SiGe-Plan am Baustart.
    if (ausfuehrung && zuordnungen.has('einweisung')) {
        if (!baustart) {
            ohneBaustart.push('Einweisung');
        } else {
            const hinweise: KickoffHinweis[] = [];
            let datum = baustart;
            if (datum < heute) {
                hinweise.push({
                    art: 'frist_ueberschritten',
                    text: `Baustart war ${datumAnzeige(baustart)} – Einweisung SiGe-Plan bitte sofort nachholen.`,
                });
                datum = heute;
            }
            termine.push(termin('einweisung', {
                datum,
                uhrzeit: uhrzeitProjekt,
                dauerMin: EINWEISUNG_DAUER_MIN,
                anzeige: datumAnzeige(datum),
            }, [`Einweisung in den SiGe-Plan zum Baustart (${datumAnzeige(baustart)}).`], hinweise));
        }
    }

    if (ohneBaustart.length > 0) {
        planHinweise.unshift({
            art: 'baustart_fehlt',
            text: `Baustart fehlt – kein Termin für ${aufzaehlung(ohneBaustart)}. Bitte Baustart im Projekt nachtragen.`,
        });
    }

    return {
        projectId,
        projectNumber: nummer,
        projectName: name,
        projectAddress: adresse,
        baustart,
        bauende,
        bauherr: meta.clientName || '',
        auftraggeber: meta.customerName || '',
        bereich: opt.bereich,
        termine,
        hinweise: planHinweise,
    };
};

// ── Kalenderdatei ───────────────────────────────────────────────────────────

/**
 * EINE Kalenderdatei mit allen Terminen aller Pläne (Sammelmail beim
 * CSV-Import, § 4.2a). Ortszeit Berlin mit VTIMEZONE, `SEQUENCE:0`. Eine
 * UID kommt nur einmal vor — doppelt in einer Datei lehnt Outlook den
 * Import ab oder legt den Termin zweimal an.
 */
export const generateKickoffIcs = (plaene: KickoffPlan[]): string => {
    const gesehen = new Set<string>();
    const events: BerlinEventInput[] = [];
    (plaene || []).forEach(plan => {
        (plan?.termine || []).forEach(t => {
            if (!t || gesehen.has(t.uid)) return;
            gesehen.add(t.uid);
            events.push({
                uid: t.uid,
                datum: t.datum,
                uhrzeit: t.uhrzeit,
                dauerMin: t.dauerMin,
                summary: t.titel,
                description: t.beschreibung,
                location: t.ort,
                rrule: t.rrule,
                sequence: 0,
                ...(t.ganztags ? { ganztags: true } : {}),
            });
        });
    });
    return generateBerlinIcs(events);
};
